<template>
    <div class="qi-customer-picker">
        <!-- Selected customer -->
        <div v-if="selected" class="qi-customer-chip">
            <div>
                <div class="font-weight-bold">{{ customerLabel(selected) }}</div>
                <small class="text-muted d-block" v-if="customerAttnLine(selected)">Attn: {{ customerAttnLine(selected) }}</small>
                <small class="text-muted" v-if="selected.phone">{{ selected.phone }}</small>
            </div>
            <button type="button" class="btn btn-sm btn-link text-muted p-0" @click="clear">
                <i class="bi bi-x-lg"></i>
            </button>
        </div>

        <!--
            Uses bootstrap-vue's <b-dropdown> (same primitive as BomList.vue's
            row-action menu) instead of a hand-rolled show/hide + document
            click-outside listener - the library handles open/close,
            positioning and outside-click reliably. <b-dropdown-form> wraps
            the search input so clicking/typing in it never closes the menu.
        -->
        <b-dropdown
            v-else
            ref="dropdown"
            variant="link"
            no-caret
            toggle-class="qi-add-card"
            menu-class="qi-customer-menu p-0 shadow-sm"
            class="w-100"
            @shown="onShown"
        >
            <template #button-content>
                <span class="qi-add-card-icon"><i class="bi bi-person-plus"></i></span>
                <span>Add a customer</span>
            </template>

            <b-dropdown-form class="p-2" @submit.prevent>
                <div class="input-group">
                    <div class="input-group-prepend">
                        <span class="input-group-text bg-white"><i class="bi bi-search text-muted"></i></span>
                    </div>
                    <input
                        ref="searchInput"
                        type="text"
                        class="form-control"
                        placeholder="Type a customer name"
                        v-model="query"
                        @input="search"
                        autocomplete="off"
                    >
                </div>
            </b-dropdown-form>

            <b-dropdown-text v-if="searching" class="text-center py-3">
                <span class="spinner-border spinner-border-sm"></span>
            </b-dropdown-text>
            <template v-else>
                <b-dropdown-item-button
                    v-for="c in results"
                    :key="c.id"
                    class="qi-customer-result"
                    @click="select(c)"
                >
                    <span class="qi-customer-result-name">
                        <span class="font-weight-bold">{{ customerLabel(c) }}</span>
                        <small class="text-muted d-block" v-if="customerAttnLine(c)">{{ customerAttnLine(c) }}</small>
                    </span>
                    <small class="text-muted qi-customer-result-contact">{{ c.phone || c.email }}</small>
                </b-dropdown-item-button>
                <b-dropdown-text v-if="!results.length" class="text-muted small text-center py-2">
                    No customer found{{ query ? ` for "${query}"` : '' }}
                </b-dropdown-text>
            </template>

            <b-dropdown-divider></b-dropdown-divider>
            <b-dropdown-item-button class="qi-customer-create" @click="openCreate(query)">
                <i class="bi bi-plus-circle mr-1"></i> Create a new customer
            </b-dropdown-item-button>
        </b-dropdown>

        <b-modal ref="add-customer-modal" hide-footer static title="Add Customer">
            <div v-if="createError" class="text-left text-danger py-2">{{ createError }}</div>

            <form @submit.prevent="createCustomer">
                <div class="form-row py-1">
                    <div class="col-6"><input class="form-control border-bottom" v-model="newCustomer.f_name" name="f_name" type="text" placeholder="First Name"></div>
                    <div class="col-6"><input class="form-control border-bottom" v-model="newCustomer.l_name" name="l_name" type="text" placeholder="Last Name"></div>
                </div>

                <div class="form-group py-1"><input class="form-control border-bottom" v-model="newCustomer.c_email" name="c_email" type="email" placeholder="Email"></div>
                <div class="form-group py-1">
                    <vue-tel-input v-on:validate="validateNewCustomerPhone" v-model="newCustomer.phone_no"></vue-tel-input>
                    <small v-if="newCustomerPhoneError" class="text-danger d-block mt-1">{{ newCustomerPhoneError }}</small>
                </div>

                <div class="form-group py-1">
                    <input class="form-control border-bottom" v-model="newCustomer.company" name="company" type="text" placeholder="Name of Company (optional)">
                </div>
                <div v-if="enableTims" class="form-group py-1">
                    <input class="form-control border-bottom" v-model="newCustomer.krapin" name="krapin" type="text" placeholder="KRA PIN">
                </div>

                <div class="form-row py-1">
                    <div class="col-6"><input class="form-control border-bottom" v-model="newCustomer.h_no" name="h_no" type="text" placeholder="House Number"></div>
                    <div class="col-6"><input class="form-control border-bottom" v-model="newCustomer.apartment" name="apartment" type="text" placeholder="Apartment"></div>
                </div>
                <div class="form-row py-1">
                    <div class="col-6"><input class="form-control border-bottom" v-model="newCustomer.street" name="street" type="text" placeholder="Street"></div>
                    <div class="col-6"><input class="form-control border-bottom" v-model="newCustomer.town" name="town" type="text" placeholder="Town"></div>
                </div>

                <div class="form-group pt-3 d-flex" style="gap:8px;">
                    <button type="button" class="btn btn-outline-secondary" @click="cancelCreate">Cancel</button>
                    <button type="submit" class="btn qi-cta-btn flex-fill" :disabled="creating || !!newCustomerPhoneError">
                        <span v-if="creating" class="spinner-border spinner-border-sm mr-1"></span> Add Customer
                    </button>
                </div>
            </form>
        </b-modal>
    </div>
</template>

<script>
import { apiErrorMessage, customerLabel, customerAttnLine } from './docHelpers';

export default {
    name: 'CustomerPicker',
    props: {
        value: { type: Object, default: null },
    },
    data() {
        return {
            query: '',
            results: [],
            searching: false,
            creating: false,
            createError: '',
            newCustomer: this.blankCustomerForm(),
            newCustomerPhoneError: '',
            debounceTimer: null,
        };
    },
    computed: {
        selected() {
            return this.value;
        },
        // Matches the "Name of Company" -> KRA PIN gating convention used on
        // the Sales page's Add Customer modal (Salespage.vue) - KRA PIN only
        // matters for a company that actually submits to eTIMS.
        enableTims() {
            const u = this.$store.getters.userDetails;
            const cs = u && u.company && u.company.companysettings;
            return !!(cs && cs.enable_tims);
        },
    },
    beforeDestroy() {
        clearTimeout(this.debounceTimer);
    },
    methods: {
        customerLabel,
        customerAttnLine,
        // Opening the dropdown shows a default customer list immediately
        // (not just once the user types), matching the reference
        // Dukakit-Enterprise-front implementation's behavior.
        onShown() {
            this.query = '';
            this.fetchResults();
            this.$nextTick(() => this.$refs.searchInput && this.$refs.searchInput.focus());
        },
        search() {
            clearTimeout(this.debounceTimer);
            this.searching = true;
            this.debounceTimer = setTimeout(this.fetchResults, 250);
        },
        async fetchResults() {
            this.searching = true;
            try {
                const params = { perPage: 10 };
                if (this.query) params.search = this.query;
                const { data } = await axios.get('/api/allcustomers', { params });
                this.results = (data.data || data || []).slice(0, 10);
            } catch (e) {
                this.results = [];
            } finally {
                this.searching = false;
            }
        },
        select(c) {
            this.query = '';
            this.$emit('input', c);
        },
        clear() {
            this.$emit('input', null);
        },
        blankCustomerForm() {
            return { f_name: '', l_name: '', c_email: '', phone_no: '', company: '', krapin: '', h_no: '', apartment: '', street: '', town: '' };
        },
        openCreate(prefillName) {
            this.newCustomer = this.blankCustomerForm();
            this.newCustomer.f_name = prefillName || '';
            this.newCustomerPhoneError = '';
            this.createError = '';
            this.$refs['add-customer-modal'].show();
        },
        cancelCreate() {
            this.$refs['add-customer-modal'].hide();
            this.newCustomer = this.blankCustomerForm();
            this.newCustomerPhoneError = '';
            this.createError = '';
        },
        validateNewCustomerPhone(phoneObject) {
            this.newCustomerPhoneError = (phoneObject.number && !phoneObject.valid) ? 'Enter a valid phone number' : '';
        },
        async createCustomer() {
            // Mirrors Salespage.vue's Add Customer validation: a customer
            // needs to be reachable by at least one of phone or email.
            if (!this.newCustomer.phone_no && !this.newCustomer.c_email) {
                this.createError = 'Enter at least a phone number or an email.';
                return;
            }
            this.creating = true;
            this.createError = '';
            try {
                const { data } = await axios.post('/api/newcustomer', this.newCustomer);
                if (data.customer) {
                    this.select(data.customer);
                    this.$refs['add-customer-modal'].hide();
                    this.newCustomer = this.blankCustomerForm();
                    this.newCustomerPhoneError = '';
                } else {
                    this.createError = data.message || 'Could not add customer.';
                }
            } catch (e) {
                this.createError = apiErrorMessage(e, 'Could not add customer.');
            } finally {
                this.creating = false;
            }
        },
    },
};
</script>

<style scoped>
/* A global unscoped rule elsewhere in the app (employee_earning.vue) sets
   `.small { margin: 150px auto }`, which leaks onto every `.small` element
   site-wide. Neutralize it here (higher specificity than the lone class). */
.small { margin: 0; }
/* Another global unscoped rule (FullCalendar.vue) sets `li { margin: 0.5em 0 }`,
   which leaks onto every <li> site-wide - including bootstrap-vue's dropdown
   item wrappers here. Neutralize it so row spacing is controlled purely by
   our own .dropdown-item padding above. */
.qi-customer-picker ::v-deep li { margin: 0; }

.qi-customer-picker .form-control {
    border-radius: 8px;
    border-color: #d7dde3;
}
.qi-customer-picker .form-control:focus {
    border-color: #b8c0c8;
    box-shadow: 0 0 0 .15rem rgba(33,37,41,.08);
}
.qi-customer-picker .input-group-text {
    border-radius: 8px 0 0 8px;
    border-color: #d7dde3;
}
.qi-customer-picker .input-group .form-control {
    border-radius: 0 8px 8px 0;
}

/* Primary call-to-action color - the platform brand color
   (see resources/js/plugins/platformBrand.js themeColor), per project
   convention: only primary/danger/secondary/dark, never a raw blue. */
.qi-cta-btn {
    background: #fd4719;
    border-color: #fd4719;
    color: #fff;
}
.qi-cta-btn:hover { background: #e23e14; border-color: #e23e14; color: #fff; }

.qi-customer-chip {
    min-height: 140px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    border: 1px solid #e2e2e2;
    border-radius: 10px;
    padding: 14px 16px;
    background: #f8f9fa;
}

/* The dropdown toggle button, styled to look like the empty-state card
   rather than a default Bootstrap button/link. */
.qi-customer-picker ::v-deep .qi-add-card {
    width: 100%;
    min-height: 140px;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 10px;
    background: #fff;
    border: 1px solid #d7dde3 !important;
    border-radius: 10px;
    color: #fd4719 !important;
    font-size: .95rem;
    text-decoration: none !important;
    box-shadow: none !important;
}
.qi-customer-picker ::v-deep .qi-add-card:hover,
.qi-customer-picker ::v-deep .qi-add-card:focus {
    border-color: #fd4719 !important;
    background: #fff6f3;
}
.qi-add-card-icon {
    width: 36px;
    height: 36px;
    flex-shrink: 0;
    border-radius: 50%;
    background: #f1f2f4;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #6c757d;
    font-size: 1.15rem;
}

.qi-customer-picker ::v-deep .qi-customer-menu {
    width: 100%;
    max-width: 100%;
    border-radius: 10px;
    border-color: #d7dde3;
    overflow: hidden;
    max-height: 340px;
    overflow-y: auto;
}
.qi-customer-picker ::v-deep .qi-customer-menu .dropdown-item {
    padding: .35rem .9rem;
    font-size: .86rem;
    line-height: 1.3;
}
.qi-customer-picker ::v-deep .qi-customer-result .dropdown-item {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 10px;
    border-bottom: 1px solid #f2f3f5;
}
.qi-customer-result-name { min-width: 0; }
.qi-customer-result-contact { flex-shrink: 0; }
.qi-customer-picker ::v-deep .qi-customer-create {
    color: #fd4719;
    font-weight: 600;
    text-align: center;
}
</style>
